import { useEffect, useMemo, useRef, useState } from "react";
import { Notice, SegmentedControl, Segment } from "@conuti-das/prince-ui";
import { Spinner } from "@/ui-prince";
import { changelogApi } from "@/lib/changelogApi";
import { flattenMigDoc, flattenAhbDoc, findBestNodeIndex, type DocNode } from "@/lib/docLocate";
import { sourceDocHref } from "@/lib/changelogLabels";
import type { ChangelogEntry } from "@/types/changelog";

type LoadState =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "ready"; nodes: DocNode[]; highlight: number };

/** Strukturierte Inline-Ansicht des Quell-Dokuments (alt/neu) mit Sprung zum ort. */
export function SourceDocViewer({ entry }: { entry: ChangelogEntry }) {
  const [fvs, setFvs] = useState<string[] | null>(null);
  const [fv, setFv] = useState(entry.formatversion);
  const [state, setState] = useState<LoadState>({ phase: "loading" });
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const ahbWithoutPruefid = entry.doctype === "ahb" && !entry.pruefid;

  // FV-Index einmal laden: alt = nächst-niedrigere verfügbare FV
  useEffect(() => {
    if (ahbWithoutPruefid) return;
    changelogApi.getDocIndex()
      .then((idx) => {
        const all = idx.doctypes[entry.doctype]?.formatversions ?? [];
        const candidates = all.filter((v) => v <= entry.formatversion).sort();
        // neu = entry.formatversion (falls vorhanden), alt = Vorgänger
        const neu = entry.formatversion;
        const alt = candidates.filter((v) => v < neu).pop();
        setFvs(alt ? [alt, neu] : [neu]);
      })
      .catch((exc) => setState({ phase: "error", message: String((exc as Error).message ?? exc) }));
  }, [entry, ahbWithoutPruefid]);

  // Dokument der gewählten FV laden
  useEffect(() => {
    if (ahbWithoutPruefid || !fvs) return;
    setState({ phase: "loading" });
    const load = entry.doctype === "mig"
      ? changelogApi.getMigDoc(fv, entry.format ?? "")
      : changelogApi.getAhbDoc(fv, entry.format ?? "", entry.pruefid ?? "");
    load
      .then((doc) => {
        const nodes = entry.doctype === "mig" ? flattenMigDoc(doc) : flattenAhbDoc(doc);
        setState({ phase: "ready", nodes, highlight: findBestNodeIndex(nodes, entry.ort) });
      })
      .catch((exc) => setState({ phase: "error", message: String((exc as Error).message ?? exc) }));
  }, [fv, fvs, entry, ahbWithoutPruefid]);

  // Nach Render die markierte Zeile in die Mitte der Liste holen. Nur die Liste
  // scrollen: scrollIntoView schöbe auch die ObjectPage darüber mit, und der
  // alt/neu-Umschalter samt Überschrift verschwände oben aus dem Blick.
  useEffect(() => {
    if (state.phase !== "ready" || state.highlight < 0) return;
    const list = listRef.current;
    const el = highlightRef.current;
    if (!list || !el) return;
    const offset = el.getBoundingClientRect().top - list.getBoundingClientRect().top;
    list.scrollTop += offset - (list.clientHeight - el.getBoundingClientRect().height) / 2;
  }, [state]);

  const neu = entry.formatversion;

  const body = useMemo(() => {
    if (state.phase !== "ready") return null;
    return (
      <div ref={listRef} data-doc-list="" style={{ maxHeight: "50vh", overflow: "auto", border: "1px solid var(--sapGroup_ContentBorderColor)", borderRadius: 4, padding: "var(--s-2)", fontFamily: "var(--font-mono)", fontSize: "0.8rem", lineHeight: 1.6 }}>
        {state.nodes.map((n, i) => (
          <div
            key={i}
            ref={i === state.highlight ? highlightRef : undefined}
            data-doc-highlight={i === state.highlight ? "true" : undefined}
            style={{
              paddingLeft: n.depth * 16,
              fontWeight: n.kind === "group" ? 700 : undefined,
              background: i === state.highlight ? "var(--sapContent_Selected_Hover_Background, #fdf3d8)" : undefined,
              borderLeft: i === state.highlight ? "3px solid var(--sapBrandColor)" : "3px solid transparent",
            }}
          >
            {n.label}
          </div>
        ))}
      </div>
    );
  }, [state]);

  if (ahbWithoutPruefid) {
    const href = sourceDocHref(entry);
    return (
      <Notice tone="info">
        Eintrag hat kein Prüfid — die AHB-Dokumentansicht ist nur mit Prüfid möglich.
        Quelle:{" "}
        {href ? (
          <a href={href} target="_blank" rel="noreferrer"><code>{entry.source_file}</code></a>
        ) : (
          <>
            <code>{entry.source_file || "—"}</code>
            {entry.source_file && " (Original-Download nur im Dev-Server)"}
          </>
        )}
      </Notice>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-2)" }}>
      {fvs && fvs.length > 1 && (
        <SegmentedControl
          selectedKeys={new Set([fv])}
          onSelectionChange={(keys) => {
            const v = [...(keys as Set<string>)][0];
            if (v) setFv(v);
          }}
        >
          {fvs.map((v) => (
            <Segment key={v} id={v}>
              {v === neu ? `${v} (neu)` : `${v} (alt)`}
            </Segment>
          ))}
        </SegmentedControl>
      )}
      {state.phase === "loading" && <Spinner active />}
      {state.phase === "error" && (
        <Notice tone="negative">{state.message}</Notice>
      )}
      {state.phase === "ready" && state.highlight < 0 && (
        <Notice tone="info">
          Stelle nicht eindeutig gefunden — Dokument ab Anfang.
        </Notice>
      )}
      {state.phase === "ready" && (
        <span style={{ fontSize: "0.75rem", color: "var(--sapContent_LabelColor)" }}>
          {entry.doctype.toUpperCase()} {entry.format} · FV {fv}
        </span>
      )}
      {body}
    </div>
  );
}
